import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, CheckCircle2, Package, XCircle } from "lucide-react";
import { StockItem } from "@/types/stock-item";
import {
  validateStockMovementStart,
  prepareStockMovementItems,
  PreparedStockMovementItem,
} from "@/lib/stock-movement-start-validation";

interface StockMovementStartDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedItems: StockItem[];
  onConfirm: (preparedItems: PreparedStockMovementItem[]) => void;
}

export function StockMovementStartDialog({
  open,
  onOpenChange,
  selectedItems,
  onConfirm,
}: StockMovementStartDialogProps) {
  const validation = validateStockMovementStart(selectedItems);
  const preparedItems = prepareStockMovementItems(validation.validItems);

  const handleConfirm = () => {
    onConfirm(preparedItems);
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Start Stock Movement
          </AlertDialogTitle>
          <AlertDialogDescription>
            {validation.canProceed ? (
              <>
                You are about to create a stock movement with{" "}
                <strong>{validation.validItems.length}</strong> item
                {validation.validItems.length !== 1 ? "s" : ""}.
              </>
            ) : (
              <>Cannot start stock movement. All selected items are inactive.</>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          {/* Warnings */}
          {validation.warnings.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="space-y-1 flex-1">
                  {validation.warnings.map((warning, index) => (
                    <p key={index} className="text-sm text-amber-800">
                      {warning}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Valid Items */}
          {validation.validItems.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <h4 className="text-sm font-semibold">
                  Items to be Pre-populated ({validation.validItems.length})
                </h4>
              </div>
              <ScrollArea className="h-[200px] rounded-md border">
                <div className="p-3 space-y-2">
                  {validation.validItems.map((item) => (
                    <div
                      key={item.code}
                      className="flex items-start justify-between gap-3 rounded-lg border bg-card p-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium">
                            {item.code}
                          </span>
                          {(!item.averageCost || item.averageCost === 0) && (
                            <Badge
                              variant="outline"
                              className="bg-amber-50 text-amber-700 border-amber-200"
                            >
                              <AlertCircle className="h-3 w-3 mr-1" />
                              No Cost
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {item.description}
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span>
                            Location: {item.warehouseCode} - {item.locationCode}
                          </span>
                          <Separator orientation="vertical" className="h-3" />
                          <span>Qty: {item.physicalQuantity}</span>
                          {item.averageCost > 0 && (
                            <>
                              <Separator orientation="vertical" className="h-3" />
                              <span>
                                Cost: £{item.averageCost.toFixed(2)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Invalid Items */}
          {validation.invalidItems.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-600" />
                <h4 className="text-sm font-semibold text-red-600">
                  Excluded Items ({validation.invalidItems.length})
                </h4>
              </div>
              <ScrollArea className="h-[150px] rounded-md border border-red-200 bg-red-50">
                <div className="p-3 space-y-2">
                  {validation.invalidItems.map((item) => (
                    <div
                      key={item.code}
                      className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-white p-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium text-red-600">
                            {item.code}
                          </span>
                          <Badge variant="destructive">Inactive</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Info Message */}
          {validation.canProceed && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="space-y-1 flex-1">
                  <p className="text-sm text-blue-800">
                    These items will be pre-populated on the Stock Movement page. You
                    can:
                  </p>
                  <ul className="text-sm text-blue-800 list-disc list-inside space-y-0.5 ml-1">
                    <li>Select the movement type</li>
                    <li>Enter quantities for each item</li>
                    <li>Change locations if needed</li>
                    <li>Add or remove items</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={!validation.canProceed}>
            Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
